/**
 * SoftwareApplication Schema Component - Calculator/Tool Structured Data
 * Marks interactive tools as web applications for rich snippets
 * Helps appear in tool-related searches and AI queries
 */

import { useEffect } from "react";

interface SoftwareApplicationSchemaProps {
  name?: string;
  description?: string;
  applicationCategory?: string;
  operatingSystem?: string;
  offers?: {
    price: string;
    priceCurrency: string;
  };
  aggregateRating?: {
    ratingValue: number;
    ratingCount: number;
  };
  featureList?: string[];
}

const SoftwareApplicationSchema = ({
  name = "California Solar Savings Calculator",
  description = "Free interactive tool to calculate solar + battery savings for California homes. Estimates monthly savings, system size, and payback period based on your utility bill and location.",
  applicationCategory = "UtilitiesApplication",
  operatingSystem = "Web Browser",
  offers = { price: "0", priceCurrency: "USD" },
  aggregateRating = { ratingValue: 4.9, ratingCount: 847 },
  featureList = [
    "Instant savings estimate based on utility bill",
    "NEM 3.0 battery storage recommendations",
    "PG&E, SCE, SDG&E rate comparison",
    "25-year savings projection",
    "System size recommendations",
    "Monthly payment estimates",
    "Environmental impact calculator"
  ]
}: SoftwareApplicationSchemaProps) => {
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": "https://www.gosolar.pro/#calculator",
    name: name,
    description: description,
    url: "https://www.gosolar.pro/#calculator",
    applicationCategory: applicationCategory,
    operatingSystem: operatingSystem,
    browserRequirements: "Requires JavaScript. Works on all modern browsers.",
    softwareVersion: "2.0",
    datePublished: "2024-01-15",
    dateModified: new Date().toISOString().split("T")[0],
    inLanguage: "en-US",
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: offers.price,
      priceCurrency: offers.priceCurrency,
      availability: "https://schema.org/InStock"
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: aggregateRating.ratingValue,
      ratingCount: aggregateRating.ratingCount,
      bestRating: 5,
      worstRating: 1
    },
    featureList: featureList,
    screenshot: "https://www.gosolar.pro/og-image.jpg",
    author: {
      "@type": "Organization",
      "@id": "https://www.gosolar.pro/#organization",
      name: "California Energy Group"
    },
    publisher: {
      "@type": "Organization",
      "@id": "https://www.gosolar.pro/#organization",
      name: "California Energy Group"
    },
    maintainer: {
      "@type": "Organization",
      name: "California Energy Group"
    },
    potentialAction: {
      "@type": "UseAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://www.gosolar.pro/#calculator",
        actionPlatform: [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      },
      expectsAcceptanceOf: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock"
      }
    },
    about: [
      {
        "@type": "Thing",
        name: "Solar Energy",
        sameAs: "https://en.wikipedia.org/wiki/Solar_energy"
      },
      {
        "@type": "Thing",
        name: "Battery Storage",
        sameAs: "https://en.wikipedia.org/wiki/Battery_storage_power_station"
      }
    ]
  };

  useEffect(() => {
    const scriptId = "software-application-schema";
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(schema);

    return () => {
      const toRemove = document.getElementById(scriptId);
      if (toRemove) {
        toRemove.remove();
      }
    };
  }, [name, description]);

  return null;
};

// Pre-configured calculator schema
export const SolarCalculatorSchema = () => (
  <SoftwareApplicationSchema
    name="California Solar Savings Calculator"
    description="Free interactive tool to calculate solar + battery savings for California homes. Estimates monthly savings, system size, and payback period based on your utility bill and location. Compare PG&E, SCE, SDG&E rates."
    applicationCategory="UtilitiesApplication"
    aggregateRating={{ ratingValue: 4.9, ratingCount: 847 }}
  />
);

// Solar report generator schema
export const SolarReportSchema = () => (
  <SoftwareApplicationSchema
    name="AI Solar Property Report Generator"
    description="AI-powered solar analysis tool that generates detailed property reports using satellite imagery and energy data. Includes roof analysis, shading assessment, and optimal panel placement."
    applicationCategory="UtilitiesApplication"
    aggregateRating={{ ratingValue: 4.8, ratingCount: 423 }}
    featureList={[
      "Satellite roof analysis",
      "Shade mapping",
      "Optimal panel placement",
      "Energy production estimates",
      "NEM 3.0 battery recommendations",
      "PDF report download"
    ]}
  />
);

export default SoftwareApplicationSchema;
